import React, { Component, ReactNode } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import adminRoutes from '../routes/adminRoutes'
import routes from '../routes/routes'
import userApi, { UserData } from '../apis/user.api'
import authService from '../services/auth.service'
import storageService from '../services/storage.service'
import menuRouteUtil from '../utils/menuRoute.util'

type AuthType = 'admin' | 'user'

interface ProtectedRouteProps extends RouteComponentProps {
  allowed: AuthType
  children: ReactNode
}

type RedirectRoutes = Record<AuthType, string>

class ProtectedRoute extends Component<ProtectedRouteProps> {
  autoLogoutRunnerId: number = 0
  autoLogoutTimeout: number | null = null

  redirectes: RedirectRoutes = {
    admin: adminRoutes.signIn,
    user: routes.signIn
  }

  currentPathname: string = ''

  constructor (props: ProtectedRouteProps) {
    super(props)
    this.handleUserInteraction = this.handleUserInteraction.bind(this)
  }

  authRedirect () {
    const authToken = storageService.get('authToken')
    if (authToken) return
    const route = this.redirectes[this.props.allowed]
    this.props.history.push(route)
  }

  timerRedirect () {
    this.props.history.push(this.redirectes.user)
    this.clearTimer()
  }

  clearTimer () {
    clearInterval(this.autoLogoutRunnerId)
    this.autoLogoutRunnerId = 0
  }

  restartTimer () {
    this.clearTimer()
    if (this.autoLogoutTimeout) {
      this.autoLogoutRunnerId = window.setTimeout(
        this.timerRedirect.bind(this),
        this.autoLogoutTimeout
      )
    }
  }

  handleUserInteraction () {
    this.restartTimer()
  }

  readUser (callback: (user: UserData) => void) {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData
    userApi.read(id, user => {
      if (!user) return
      callback(user)
    })
  }

  autoLogout () {
    if (this.props.allowed === 'user') {
      this.readUser(user => {
        this.autoLogoutTimeout = user.timeout * 60 * 1000
        this.restartTimer()
      })
    }
  }

  notPermittedRedirect () {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData
    const route = menuRouteUtil.getCurrentRouteName()
    if (!route) return
    userApi.hasPermission(id, route, permission => {
      if (permission && !permission.has) {
        const route = routes[permission.redirectModule]
        this.props.history.push(route)
      }
    })
  }

  updateCurrentPathname () {
    this.currentPathname = window.location.pathname
  }

  componentWillUnmount () {
    this.clearTimer()
  }

  componentDidMount () {
    this.authRedirect()
    this.autoLogout()
    this.notPermittedRedirect()
    this.updateCurrentPathname()
  }

  componentDidUpdate () {
    const { pathname } = window.location
    if (this.currentPathname !== pathname) {
      this.notPermittedRedirect()
    }
    this.updateCurrentPathname()
  }

  render () {
    return (
      <div
        onMouseMove={this.handleUserInteraction}
        onKeyUp={this.handleUserInteraction}>
        {this.props.children}
      </div>
    )
  }
}

export default withRouter(ProtectedRoute)
